import { useMemo, useState } from 'react';
import { useTriggerStream } from '@/hooks/useTriggerStream';
import { useHeartbeats } from '@/hooks/useHeartbeats';
import { useDeviceMetrics } from '@/hooks/useDeviceMetrics';
import { VizControls } from '@/components/viz/VizControls';
import { FlowRiverGallery } from '@/components/viz/FlowRiverGallery';
import { CumulativeConstellationClock } from '@/components/viz/CumulativeConstellationClock';
import { PondRipples } from '@/components/viz/PondRipples';
import { UptimeTimeline } from '@/components/viz/UptimeTimeline';
import { SignalField } from '@/components/viz/SignalField';
import { MyceliumNetwork } from '@/components/viz/MyceliumNetwork';
import { PatternTapestry } from '@/components/viz/PatternTapestry';
import { buildDeviceColorMap } from '@/components/viz/vizPalette';
import { Seo } from '@/components/Seo';
import { useLang, useT } from '@/i18n/LanguageContext';
import { visualizeContent } from '@/i18n/content/visualize';
import { pathFor } from '@/i18n/routes';
import { useTheme } from '@/contexts/ThemeContext';

interface Section {
  num: string;
  title: string;
  desc: string;
  render: () => JSX.Element;
}

export default function Visualize() {
  const lang = useLang();
  const t = useT(visualizeContent);
  const [realtime] = useState(true);
  const { isDark: dark } = useTheme();

  // One archive-wide stream feeds every chart — avoids a second duplicate fetch.
  const { triggers: riverTriggers, devices: historyDevices, loading, streaming, totalCount } = useTriggerStream('10y', realtime);
  // One heartbeat fetch feeds the heartbeat, signal and breathing sections.
  const { beats, missing: heartbeatsMissing } = useHeartbeats();
  // Server-aggregated pattern history (needs the SQL function from
  // docs/sql/viz_metric_functions.sql).
  const { patternChanges, missing: metricsMissing } = useDeviceMetrics();
  const historyLoading = false;

  // Every device ever seen — including ones that have been quiet for a long time.
  const allDevices = useMemo(() => {
    const map = new Map(historyDevices.map((d) => [d.device_id, d]));
    riverTriggers.forEach((t) => {
      if (!map.has(t.device_id)) {
        map.set(t.device_id, { device_id: t.device_id, name: null, hardware_type: null, last_seen: null });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.device_id.localeCompare(b.device_id));
  }, [historyDevices, riverTriggers]);

  // Shared colours for the metric charts, stable across sections.
  const metricColorMap = useMemo(
    () =>
      buildDeviceColorMap([
        ...allDevices.map((d) => d.device_id),
        ...(beats ?? []).map((b) => b.device_id),
      ]),
    [allDevices, beats],
  );

  const sections: Section[] = [
    {
      num: '01',
      title: t.sections.klok.title,
      desc: t.sections.klok.desc,
      render: () => <CumulativeConstellationClock triggers={riverTriggers} dark={dark} />,
    },
    {
      num: '02',
      title: t.sections.rimpelingen.title,
      desc: t.sections.rimpelingen.desc,
      render: () => <PondRipples triggers={riverTriggers} dark={dark} />,
    },
    {
      num: '03',
      title: t.sections.stroom.title,
      desc: t.sections.stroom.desc,
      render: () => <FlowRiverGallery triggers={riverTriggers} devices={allDevices} dark={dark} />,
    },
    {
      num: '04',
      title: t.sections.mycelium.title,
      desc: t.sections.mycelium.desc,
      render: () => <MyceliumNetwork triggers={riverTriggers} devices={allDevices} />,
    },
    {
      num: '05',
      title: t.sections.signaal.title,
      desc: t.sections.signaal.desc,
      render: () => <SignalField beats={beats ?? []} devices={allDevices} colorMap={metricColorMap} />,
    },
    {
      num: '06',
      title: t.sections.weefsel.title,
      desc: t.sections.weefsel.desc,
      render: () =>
        metricsMissing ? (
          <p className="py-10 text-center font-mono text-xs leading-relaxed text-sitefg/45">
            {t.charts.metricsMissing}
          </p>
        ) : (
          <PatternTapestry changes={patternChanges ?? []} devices={allDevices} />
        ),
    },
    {
      num: '07',
      title: t.sections.hartslag.title,
      desc: t.sections.hartslag.desc,
      render: () => <UptimeTimeline beats={beats} missing={heartbeatsMissing} />,
    },
  ];

  return (
    <div className="min-h-screen -m-6 bg-sitebg text-sitefg">
      <Seo
        title={t.seoTitle}
        description={t.seoDescription}
        path={pathFor('visualisatie', lang)}
      />
      <VizControls
        triggerCount={totalCount}
        deviceCount={allDevices.length}
        counting={streaming}
      />

      {/* Hero */}
      <header className="max-w-3xl mx-auto px-6 pt-16 pb-8">
        <h1 className="text-3xl md:text-5xl font-light tracking-tight">
          {t.title}
        </h1>
        <h2 className="mt-6 text-2xl md:text-3xl font-light leading-[1.1] tracking-tight">
          {t.headingBefore}
          <span className="italic text-[var(--viz-accent)]">{t.headingAccent}</span>
          {t.headingAfter}
        </h2>
        <p className="mt-4 text-base leading-relaxed text-sitefg/60">
          {t.lead}
        </p>
      </header>

      {loading && (
        <div className="max-w-3xl mx-auto px-6 py-20 text-center text-sitefg/40">
          {t.loading}
        </div>
      )}

      {!loading && !historyLoading &&
        sections.map((s) => (
          <section key={s.num} className="max-w-3xl mx-auto px-6 py-14 border-t border-sitefg/10">
            <div className="mb-8">
              <div className="text-sm font-mono text-[var(--viz-accent)]">{s.num}</div>
              <h2 className="mt-2 text-2xl font-light tracking-tight">{s.title}</h2>
              <p className="mt-3 text-sm leading-relaxed text-sitefg/55">{s.desc}</p>
            </div>
            {s.render()}
          </section>
        ))}
    </div>
  );
}
